import React, { useState } from 'react';
import {
  LayoutDashboard, BookOpen, Users, BarChart3, Settings, Plus, Edit, Trash2, Eye,
  Save, X, Star, TrendingUp, DollarSign, MessageSquare,
  Search, Bell, LogOut, CheckCircle, Menu, Target, ChevronLeft, ChevronRight, Bookmark
} from 'lucide-react';
import { Helmet } from 'react-helmet';

interface Course {
  id: string; title: string; description: string; category: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced'; price: number;
  status: 'published' | 'draft' | 'under_review'; students: number;
  rating: number; reviews: number; revenue: number; createdAt: string;
  updatedAt: string; thumbnail?: string; chapters: Chapter[];
}
interface Chapter { id: string; title: string; pages: Page[]; }
interface Page { id: string; content: string; type: 'text' | 'image' | 'video' | 'quiz'; title?: string; }
interface Student { id: string; name: string; email: string; avatar?: string; enrolledAt: string; progress: number; lastActive: string; }
interface MentorDashboardProps { onLogout: () => void; }

const MentorDashboard: React.FC<MentorDashboardProps> = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showCourseEditor, setShowCourseEditor] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [showCourseViewer, setShowCourseViewer] = useState(false);
  const [viewingCourse, setViewingCourse] = useState<Course | null>(null);
  const [currentChapter, setCurrentChapter] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  const mentorProfile = {
    name: 'Dr. Rajesh Mehta', email: 'rajesh@example.com',
    avatar: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop',
    bio: 'Senior AI Researcher with 15+ years in financial technology',
    expertise: ['AI Trading', 'Machine Learning', 'Risk Management'],
    joinedAt: '2023-01-15', verified: true, rating: 4.9, totalStudents: 3250, totalRevenue: 485000, coursesPublished: 5
  };

  const [courses, setCourses] = useState<Course[]>([
    {
      id: '1', title: 'AI-Powered Stock Market Mastery', description: 'Master the art of AI-driven stock market analysis and predictions.',
      category: 'AI Trading', level: 'Intermediate', price: 4999, status: 'published',
      students: 1250, rating: 4.9, reviews: 342, revenue: 425000, createdAt: '2023-06-15', updatedAt: '2024-01-10',
      thumbnail: 'https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg?auto=compress&cs=tinysrgb&w=400&h=200&fit=crop',
      chapters: [{ id: 'ch1', title: 'Introduction to AI in Trading', pages: [
        { id: 'p1', type: 'text', title: 'Welcome to AI Trading', content: `<h1>Welcome to AI-Powered Stock Market Mastery</h1><p>In this comprehensive course, you'll discover how artificial intelligence is revolutionizing the world of stock market trading and investment.</p><h2>What You'll Learn</h2><ul><li>Fundamentals of AI in financial markets</li><li>Machine learning algorithms for market prediction</li><li>Risk management with AI tools</li></ul>` },
        { id: 'p2', type: 'text', title: 'The Evolution of Trading', content: `<h1>The Evolution of Trading Technology</h1><p>Trading has evolved dramatically from manual floor trading to algorithmic and AI-powered systems.</p><h2>Key Milestones</h2><ul><li><strong>1970s:</strong> Electronic trading systems emerge</li><li><strong>2020s:</strong> AI and machine learning integration</li></ul>` }
      ]}]
    },
    {
      id: '2', title: 'Advanced Technical Analysis', description: 'Deep dive into advanced chart patterns and technical indicators.',
      category: 'Technical Analysis', level: 'Advanced', price: 3999, status: 'published',
      students: 890, rating: 4.8, reviews: 234, revenue: 285000, createdAt: '2023-08-20', updatedAt: '2024-01-05',
      thumbnail: 'https://images.pexels.com/photos/6802049/pexels-photo-6802049.jpeg?auto=compress&cs=tinysrgb&w=400&h=200&fit=crop',
      chapters: [{ id: 'ch1', title: 'Advanced Chart Patterns', pages: [{ id: 'p1', type: 'text', title: 'Complex Pattern Recognition', content: `<h1>Advanced Chart Patterns</h1><p>Complex chart patterns that provide powerful trading signals when properly identified.</p>` }] }]
    },
    {
      id: '3', title: 'Risk Management Strategies', description: 'Comprehensive guide to protecting your trading capital.',
      category: 'Risk Management', level: 'Beginner', price: 2999, status: 'draft',
      students: 0, rating: 0, reviews: 0, revenue: 0, createdAt: '2024-01-01', updatedAt: '2024-01-15',
      chapters: [{ id: 'ch1', title: 'Risk Fundamentals', pages: [{ id: 'p1', type: 'text', title: 'Understanding Risk', content: `<h1>What is Risk in Trading?</h1><p>Risk is the possibility of losing money on an investment.</p>` }] }]
    }
  ]);

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard className="w-5 h-5" /> },
    { id: 'courses', label: 'My Courses', icon: <BookOpen className="w-5 h-5" />, count: courses.length },
    { id: 'students', label: 'Students', icon: <Users className="w-5 h-5" />, count: mentorProfile.totalStudents },
    { id: 'analytics', label: 'Analytics', icon: <BarChart3 className="w-5 h-5" /> },
    { id: 'settings', label: 'Settings', icon: <Settings className="w-5 h-5" /> }
  ];

  const getAllPages = (course: Course) => {
    const allPages: { page: Page; chapterIndex: number; pageIndex: number; chapterTitle: string }[] = [];
    course.chapters.forEach((chapter, chapterIndex) => {
      chapter.pages.forEach((page, pageIndex) => allPages.push({ page, chapterIndex, pageIndex, chapterTitle: chapter.title }));
    });
    return allPages;
  };

  const getCurrentAbsolutePageIndex = (course: Course, chapterIndex: number, pageIndex: number) => {
    let absoluteIndex = 0;
    for (let i = 0; i < chapterIndex; i++) absoluteIndex += course.chapters[i].pages.length;
    return absoluteIndex + pageIndex;
  };

  const nextPage = () => {
    if (!viewingCourse) return;
    const allPages = getAllPages(viewingCourse);
    const currentAbsoluteIndex = getCurrentAbsolutePageIndex(viewingCourse, currentChapter, currentPage);
    if (currentAbsoluteIndex < allPages.length - 1) {
      const nextPageData = allPages[currentAbsoluteIndex + 1];
      setCurrentChapter(nextPageData.chapterIndex);
      setCurrentPage(nextPageData.pageIndex);
    }
  };

  const prevPage = () => {
    if (!viewingCourse) return;
    const currentAbsoluteIndex = getCurrentAbsolutePageIndex(viewingCourse, currentChapter, currentPage);
    if (currentAbsoluteIndex > 0) {
      const allPages = getAllPages(viewingCourse);
      const prevPageData = allPages[currentAbsoluteIndex - 1];
      setCurrentChapter(prevPageData.chapterIndex);
      setCurrentPage(prevPageData.pageIndex);
    }
  };

  const handleViewCourse = (course: Course) => { setViewingCourse(course); setCurrentChapter(0); setCurrentPage(0); setShowCourseViewer(true); };
  const handleCreateCourse = () => { setEditingCourse(null); setShowCourseEditor(true); };
  const handleEditCourse = (course: Course) => { setEditingCourse(course); setShowCourseEditor(true); };
  const handleDeleteCourse = (courseId: string) => { if (confirm('Are you sure you want to delete this course?')) setCourses(courses.filter(c => c.id !== courseId)); };

  const dashboardStats = {
    totalRevenue: mentorProfile.totalRevenue, totalStudents: mentorProfile.totalStudents,
    activeCourses: courses.filter(c => c.status === 'published').length, avgRating: mentorProfile.rating,
    monthlyRevenue: 85000, newStudents: 156, courseViews: 12450, completionRate: 78
  };

  // Course Viewer
  if (showCourseViewer && viewingCourse) {
    const allPages = getAllPages(viewingCourse);
    const currentAbsoluteIndex = getCurrentAbsolutePageIndex(viewingCourse, currentChapter, currentPage);
    const currentPageData = allPages[currentAbsoluteIndex];
    const nextPageData = currentAbsoluteIndex < allPages.length - 1 ? allPages[currentAbsoluteIndex + 1] : null;
    const isFirstPage = currentAbsoluteIndex === 0;
    const isLastPage = currentAbsoluteIndex === allPages.length - 1;

    return (
      <div className="fixed inset-0 bg-slate-100 z-50 overflow-hidden flex flex-col">
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button onClick={() => setShowCourseViewer(false)} className="text-slate-400 hover:text-slate-700 transition-colors" aria-label="Close course viewer"><X className="w-6 h-6" aria-hidden="true" /></button>
              <div><h1 className="text-lg font-bold text-slate-900">{viewingCourse.title}</h1><p className="text-sm text-slate-500">{currentPageData.chapterTitle} · Page {currentAbsoluteIndex + 1} of {allPages.length}</p></div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-slate-500">Progress: {Math.round(((currentAbsoluteIndex + 1) / allPages.length) * 100)}%</div>
              <button className="text-slate-400 hover:text-amber-500 transition-colors"><Bookmark className="w-5 h-5" /></button>
            </div>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-8 overflow-hidden">
          <div className="max-w-6xl w-full flex items-center justify-center relative h-full max-h-[600px]">
            <button onClick={prevPage} disabled={isFirstPage} className={`absolute left-0 z-10 p-3 rounded-full transition-all ${isFirstPage ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:scale-110 shadow-sm'}`}><ChevronLeft className="w-6 h-6" /></button>
            <button onClick={nextPage} disabled={isLastPage} className={`absolute right-0 z-10 p-3 rounded-full transition-all ${isLastPage ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:scale-110 shadow-sm'}`}><ChevronRight className="w-6 h-6" /></button>
            <div className="flex w-full max-w-5xl h-full shadow-xl rounded-xl overflow-hidden border border-slate-200">
              <div className="w-1/2 bg-white p-8 border-r border-slate-200 overflow-y-auto">
                <div className="h-full flex flex-col">
                  <div className="mb-6"><h2 className="text-xl font-bold text-slate-900 mb-2 font-serif">{currentPageData.page.title || currentPageData.chapterTitle}</h2><div className="w-10 h-1 bg-navy-500 rounded"></div></div>
                  <div className="flex-1 prose prose-slate max-w-none text-sm" dangerouslySetInnerHTML={{ __html: currentPageData.page.content }} />
                  <div className="mt-6 pt-4 border-t border-slate-100 text-xs text-slate-400">{currentPageData.chapterTitle} · Page {currentAbsoluteIndex + 1}</div>
                </div>
              </div>
              <div className="w-1/2 bg-slate-50 p-8 overflow-y-auto">
                <div className="h-full flex flex-col">
                  {nextPageData ? (
                    <>
                      <div className="mb-6"><h2 className="text-xl font-bold text-slate-900 mb-2 font-serif">{nextPageData.page.title || nextPageData.chapterTitle}</h2><div className="w-10 h-1 bg-slate-300 rounded"></div></div>
                      <div className="flex-1 prose prose-slate max-w-none text-sm" dangerouslySetInnerHTML={{ __html: nextPageData.page.content }} />
                      <div className="mt-6 pt-4 border-t border-slate-200 text-xs text-slate-400">{nextPageData.chapterTitle} · Page {currentAbsoluteIndex + 2}</div>
                    </>
                  ) : (
                    <div className="flex-1 flex flex-col justify-center items-center text-center">
                      <div className="bg-green-50 border border-green-200 p-8 rounded-2xl mb-6">
                        <CheckCircle className="w-14 h-14 text-green-600 mx-auto mb-4" />
                        <h3 className="text-lg font-bold text-slate-900 mb-2 font-serif">Course Preview Complete!</h3>
                        <p className="text-slate-600 text-sm">You've reached the end of this course preview.</p>
                      </div>
                      <div className="space-y-3 w-full">
                        <button onClick={() => handleEditCourse(viewingCourse)} className="w-full bg-navy-500 hover:bg-navy-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors text-sm">Edit Course</button>
                        <button onClick={() => setShowCourseViewer(false)} className="w-full border border-slate-300 text-slate-700 px-6 py-3 rounded-lg font-semibold hover:bg-slate-50 transition-all text-sm">Back to Dashboard</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white border-t border-slate-200 px-6 py-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-500">{isLastPage ? 'Course Preview Complete!' : `${currentPageData.chapterTitle} — ${currentPageData.page.title || 'Page ' + (currentAbsoluteIndex + 1)}`}</div>
            <div className="w-48 bg-slate-200 rounded-full h-1.5"><div className="bg-navy-500 h-1.5 rounded-full transition-all duration-300" style={{ width: `${((currentAbsoluteIndex + 1) / allPages.length) * 100}%` }}></div></div>
          </div>
        </div>
      </div>
    );
  }

  if (showCourseEditor) {
    return <CourseEditor
      course={editingCourse}
      onSave={(courseData) => {
        if (editingCourse) {
          setCourses(courses.map(c => c.id === editingCourse.id ? { ...c, ...courseData } : c));
        } else {
          const newCourse: Course = { id: Date.now().toString(), ...courseData, students: 0, rating: 0, reviews: 0, revenue: 0, createdAt: new Date().toISOString().split('T')[0], updatedAt: new Date().toISOString().split('T')[0], chapters: courseData.chapters || [] } as Course;
          setCourses([newCourse, ...courses]);
        }
        setShowCourseEditor(false); setEditingCourse(null);
      }}
      onCancel={() => { setShowCourseEditor(false); setEditingCourse(null); }}
    />;
  }

  return (
    <>
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="min-h-screen bg-slate-50 flex">
        {/* Sidebar */}
        <div className={`bg-white border-r border-slate-200 transition-all duration-300 ${sidebarCollapsed ? 'w-16' : 'w-64'} flex flex-col`}>
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center justify-between">
              {!sidebarCollapsed && (
                <div className="flex items-center space-x-2">
                  <div className="bg-navy-500 p-2 rounded-lg"><BookOpen className="w-5 h-5 text-white" /></div>
                  <span className="font-bold text-slate-900 text-sm">Mentor Portal</span>
                </div>
              )}
              <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="p-1 hover:bg-slate-100 rounded text-slate-500"><Menu className="w-5 h-5" /></button>
            </div>
          </div>

          <nav className="flex-1 p-3">
            <ul className="space-y-1">
              {sidebarItems.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${activeTab === item.id ? 'bg-navy-500 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
                  >
                    {item.icon}
                    {!sidebarCollapsed && (
                      <>
                        <span className="flex-1 text-left">{item.label}</span>
                        {item.count !== undefined && item.count > 0 && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === item.id ? 'bg-white/20 text-white' : 'bg-navy-100 text-navy-600'}`}>{item.count}</span>
                        )}
                      </>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <div className="p-3 border-t border-slate-100">
            <div className="flex items-center space-x-3">
              <img src={mentorProfile.avatar} alt={mentorProfile.name} className="w-8 h-8 rounded-full flex-shrink-0" />
              {!sidebarCollapsed && (
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{mentorProfile.name}</div>
                  <div className="text-xs text-slate-500">Mentor</div>
                </div>
              )}
              <button onClick={onLogout} className="text-slate-400 hover:text-red-500 transition-colors flex-shrink-0" title="Logout"><LogOut className="w-4 h-4" /></button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          <header className="bg-white border-b border-slate-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-bold text-slate-900 capitalize font-serif">{activeTab}</h1>
              <div className="flex items-center space-x-4">
                <button className="relative p-2 text-slate-500 hover:text-slate-900">
                  <Bell className="w-5 h-5" />
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">3</span>
                </button>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-slate-500">Revenue:</span>
                  <span className="text-base font-bold text-green-600">₹{dashboardStats.totalRevenue.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </header>

          <main className="flex-1 p-6 overflow-auto">
            {activeTab === 'dashboard' && <DashboardContent stats={dashboardStats} courses={courses} mentorProfile={mentorProfile} />}
            {activeTab === 'courses' && <CoursesContent courses={courses} onCreateCourse={handleCreateCourse} onEditCourse={handleEditCourse} onDeleteCourse={handleDeleteCourse} onViewCourse={handleViewCourse} />}
            {activeTab === 'students' && <StudentsContent courses={courses} />}
            {activeTab === 'analytics' && <AnalyticsContent courses={courses} stats={dashboardStats} />}
            {activeTab === 'settings' && <SettingsContent mentorProfile={mentorProfile} />}
          </main>
        </div>
      </div>
    </>
  );
};

const DashboardContent: React.FC<{ stats: any; courses: Course[]; mentorProfile: any }> = ({ stats, courses, mentorProfile }) => (
  <div className="space-y-6">
    <div className="bg-navy-950 rounded-2xl p-8 text-white">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold mb-2 font-serif">Welcome back, {mentorProfile.name}!</h2>
          <p className="text-white/70">You've taught {stats.totalStudents.toLocaleString()} students and earned ₹{stats.totalRevenue.toLocaleString()} so far.</p>
        </div>
        <div className="bg-white/10 rounded-xl p-4 text-right">
          <div className="text-2xl font-bold">₹{stats.monthlyRevenue.toLocaleString()}</div>
          <div className="text-white/60 text-sm">This Month</div>
        </div>
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
      {[
        { label: 'Total Revenue', value: `₹${stats.totalRevenue.toLocaleString()}`, icon: <DollarSign className="w-5 h-5 text-green-600" />, bg: 'bg-green-50' },
        { label: 'Total Students', value: stats.totalStudents.toLocaleString(), icon: <Users className="w-5 h-5 text-navy-500" />, bg: 'bg-navy-50' },
        { label: 'Active Courses', value: stats.activeCourses, icon: <BookOpen className="w-5 h-5 text-blue-600" />, bg: 'bg-blue-50' },
        { label: 'Average Rating', value: stats.avgRating, icon: <Star className="w-5 h-5 text-amber-500" />, bg: 'bg-amber-50' },
      ].map((stat) => (
        <div key={stat.label} className="bg-white p-6 rounded-xl border border-slate-200">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-slate-500 mb-1">{stat.label}</p><p className="text-2xl font-bold text-slate-900">{stat.value}</p></div>
            <div className={`${stat.bg} p-3 rounded-lg`}>{stat.icon}</div>
          </div>
        </div>
      ))}
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-5 border-b border-slate-100"><h3 className="text-base font-semibold text-slate-900">Recent Courses</h3></div>
        <div className="p-5 space-y-4">
          {courses.slice(0, 5).map((course) => (
            <div key={course.id} className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0"><BookOpen className="w-5 h-5 text-slate-500" /></div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-slate-900 truncate text-sm">{course.title}</h4>
                <p className="text-xs text-slate-500">{course.students} students · ₹{course.revenue.toLocaleString()}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${course.status === 'published' ? 'bg-green-100 text-green-700' : course.status === 'draft' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{course.status}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-5 border-b border-slate-100"><h3 className="text-base font-semibold text-slate-900">This Month</h3></div>
        <div className="p-5 space-y-5">
          {[
            { label: 'New Students', sub: `+${stats.newStudents} this month`, value: `+${stats.newStudents}`, icon: <TrendingUp className="w-5 h-5 text-green-600" />, bg: 'bg-green-50', color: 'text-green-600' },
            { label: 'Course Views', sub: 'Total views', value: stats.courseViews.toLocaleString(), icon: <Eye className="w-5 h-5 text-navy-500" />, bg: 'bg-navy-50', color: 'text-navy-500' },
            { label: 'Completion Rate', sub: 'Average across courses', value: `${stats.completionRate}%`, icon: <Target className="w-5 h-5 text-purple-600" />, bg: 'bg-purple-50', color: 'text-purple-600' },
          ].map((item) => (
            <div key={item.label} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`${item.bg} p-2 rounded-lg`}>{item.icon}</div>
                <div><div className="font-medium text-slate-900 text-sm">{item.label}</div><div className="text-xs text-slate-500">{item.sub}</div></div>
              </div>
              <div className={`text-xl font-bold ${item.color}`}>{item.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const CoursesContent: React.FC<{ courses: Course[]; onCreateCourse: () => void; onEditCourse: (course: Course) => void; onDeleteCourse: (courseId: string) => void; onViewCourse: (course: Course) => void }> = ({ courses, onCreateCourse, onEditCourse, onDeleteCourse, onViewCourse }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || course.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <input type="text" placeholder="Search courses..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-300 text-sm" />
          </div>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-300 text-sm">
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="under_review">Under Review</option>
          </select>
        </div>
        <button onClick={onCreateCourse} className="bg-navy-500 hover:bg-navy-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 text-sm font-medium">
          <Plus className="w-4 h-4" /><span>Create New Course</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCourses.map((course) => (
          <div key={course.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md hover:border-navy-300 transition-all">
            <div className="relative">
              {course.thumbnail ? (
                <img src={course.thumbnail} alt={course.title} className="w-full h-40 object-cover" />
              ) : (
                <div className="w-full h-40 bg-navy-50 flex items-center justify-center"><BookOpen className="w-10 h-10 text-navy-300" /></div>
              )}
              <div className="absolute top-3 left-3">
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${course.status === 'published' ? 'bg-green-500 text-white' : course.status === 'draft' ? 'bg-amber-500 text-white' : 'bg-blue-500 text-white'}`}>{course.status}</span>
              </div>
            </div>
            <div className="p-5">
              <h3 className="text-base font-bold mb-1 text-slate-900 line-clamp-2 font-serif">{course.title}</h3>
              <p className="text-slate-500 mb-3 line-clamp-2 text-sm">{course.description}</p>
              <div className="grid grid-cols-2 gap-2 mb-3 text-xs text-slate-500">
                <div className="flex items-center space-x-1"><Users className="w-3.5 h-3.5" /><span>{course.students.toLocaleString()}</span></div>
                <div className="flex items-center space-x-1"><DollarSign className="w-3.5 h-3.5" /><span>₹{course.price.toLocaleString()}</span></div>
                <div className="flex items-center space-x-1"><Star className="w-3.5 h-3.5 text-amber-400 fill-current" /><span>{course.rating || 'No ratings'}</span></div>
                <div className="flex items-center space-x-1"><TrendingUp className="w-3.5 h-3.5" /><span>₹{course.revenue.toLocaleString()}</span></div>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                <div className="flex items-center space-x-1">
                  <button onClick={() => onViewCourse(course)} className="p-1.5 text-slate-400 hover:text-navy-500 transition-colors" title="View"><Eye className="w-4 h-4" /></button>
                  <button onClick={() => onEditCourse(course)} className="p-1.5 text-slate-400 hover:text-blue-600 transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>
                  <button onClick={() => onDeleteCourse(course.id)} className="p-1.5 text-slate-400 hover:text-red-500 transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="text-xs text-slate-400">Updated {new Date(course.updatedAt).toLocaleDateString()}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredCourses.length === 0 && (
        <div className="text-center py-16">
          <BookOpen className="w-14 h-14 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No courses found</h3>
          <p className="text-slate-500 mb-6 text-sm">{searchTerm || filterStatus !== 'all' ? 'Try adjusting your search or filter criteria.' : 'Create your first course to get started.'}</p>
          <button onClick={onCreateCourse} className="bg-navy-500 hover:bg-navy-600 text-white px-6 py-3 rounded-lg transition-colors text-sm font-medium">Create Your First Course</button>
        </div>
      )}
    </div>
  );
};

const StudentsContent: React.FC<{ courses: Course[] }> = () => {
  const students: Student[] = [
    { id: '1', name: 'Rahul Sharma', email: 'rahul@example.com', avatar: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&fit=crop', enrolledAt: '2024-01-10', progress: 75, lastActive: '2024-01-15' },
    { id: '2', name: 'Priya Patel', email: 'priya@example.com', enrolledAt: '2024-01-08', progress: 45, lastActive: '2024-01-14' },
    { id: '3', name: 'Amit Kumar', email: 'amit@example.com', enrolledAt: '2024-01-05', progress: 90, lastActive: '2024-01-15' }
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {[
          { label: 'Total Students', value: students.length, icon: <Users className="w-5 h-5 text-navy-500" />, bg: 'bg-navy-50' },
          { label: 'Active This Week', value: students.filter(s => new Date(s.lastActive) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length, icon: <TrendingUp className="w-5 h-5 text-green-600" />, bg: 'bg-green-50' },
          { label: 'Avg Progress', value: `${Math.round(students.reduce((sum, s) => sum + s.progress, 0) / students.length)}%`, icon: <Target className="w-5 h-5 text-purple-600" />, bg: 'bg-purple-50' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white p-6 rounded-xl border border-slate-200">
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-slate-500 mb-1">{stat.label}</p><p className="text-2xl font-bold text-slate-900">{stat.value}</p></div>
              <div className={`${stat.bg} p-3 rounded-lg`}>{stat.icon}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100"><h3 className="text-base font-semibold text-slate-900">All Students</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>{['Student', 'Enrolled', 'Progress', 'Last Active', 'Actions'].map(h => <th key={h} className="text-left px-5 py-3.5 font-medium text-slate-700">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {students.map((student) => (
                <tr key={student.id} className="hover:bg-slate-50">
                  <td className="px-5 py-4">
                    <div className="flex items-center space-x-3">
                      <img src={student.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(student.name)}&background=1B4FD8&color=fff`} alt={student.name} className="w-9 h-9 rounded-full" />
                      <div><div className="font-medium text-slate-900">{student.name}</div><div className="text-xs text-slate-500">{student.email}</div></div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-slate-600">{new Date(student.enrolledAt).toLocaleDateString()}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-24 bg-slate-200 rounded-full h-1.5"><div className="bg-navy-500 h-1.5 rounded-full" style={{ width: `${student.progress}%` }}></div></div>
                      <span className="text-slate-600 text-xs">{student.progress}%</span>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-slate-600">{new Date(student.lastActive).toLocaleDateString()}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center space-x-1">
                      <button className="p-1.5 text-slate-400 hover:text-navy-500 transition-colors"><MessageSquare className="w-4 h-4" /></button>
                      <button className="p-1.5 text-slate-400 hover:text-green-600 transition-colors"><Eye className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const AnalyticsContent: React.FC<{ courses: Course[]; stats: any }> = ({ courses, stats }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
      {[
        { label: 'Total Revenue', value: `₹${stats.totalRevenue.toLocaleString()}`, icon: <DollarSign className="w-5 h-5 text-green-600" />, bg: 'bg-green-50' },
        { label: 'Monthly Revenue', value: `₹${stats.monthlyRevenue.toLocaleString()}`, icon: <TrendingUp className="w-5 h-5 text-navy-500" />, bg: 'bg-navy-50' },
        { label: 'Course Views', value: stats.courseViews.toLocaleString(), icon: <Eye className="w-5 h-5 text-purple-600" />, bg: 'bg-purple-50' },
        { label: 'Completion Rate', value: `${stats.completionRate}%`, icon: <Target className="w-5 h-5 text-amber-500" />, bg: 'bg-amber-50' },
      ].map((stat) => (
        <div key={stat.label} className="bg-white p-6 rounded-xl border border-slate-200">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-slate-500 mb-1">{stat.label}</p><p className="text-2xl font-bold text-slate-900">{stat.value}</p></div>
            <div className={`${stat.bg} p-3 rounded-lg`}>{stat.icon}</div>
          </div>
        </div>
      ))}
    </div>
    <div className="bg-white rounded-xl border border-slate-200">
      <div className="p-5 border-b border-slate-100"><h3 className="text-base font-semibold text-slate-900">Top Performing Courses</h3></div>
      <div className="p-5 space-y-4">
        {courses.sort((a, b) => b.revenue - a.revenue).slice(0, 5).map((course, index) => (
          <div key={course.id} className="flex items-center space-x-4">
            <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-sm font-medium text-slate-600 flex-shrink-0">{index + 1}</div>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-slate-900 text-sm truncate">{course.title}</h4>
              <div className="flex items-center space-x-4 text-xs text-slate-500 mt-0.5">
                <span className="flex items-center space-x-1"><Users className="w-3.5 h-3.5" /><span>{course.students.toLocaleString()}</span></span>
                <span className="flex items-center space-x-1"><Star className="w-3.5 h-3.5 text-amber-400 fill-current" /><span>{course.rating || 'N/A'}</span></span>
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-sm font-medium text-slate-900">₹{course.revenue.toLocaleString()}</div>
              <div className="text-xs text-slate-500">{course.students} students</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const SettingsContent: React.FC<{ mentorProfile: any }> = ({ mentorProfile }) => {
  const [profile, setProfile] = useState(mentorProfile);
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-5 border-b border-slate-100"><h3 className="text-base font-semibold text-slate-900">Profile Settings</h3></div>
        <div className="p-6 space-y-5">
          <div className="flex items-center space-x-5">
            <img src={profile.avatar} alt={profile.name} className="w-16 h-16 rounded-full border-2 border-slate-200" />
            <div>
              <button className="bg-navy-500 hover:bg-navy-600 text-white px-4 py-2 rounded-lg transition-colors text-sm">Change Photo</button>
              <p className="text-xs text-slate-500 mt-1.5">JPG, PNG or GIF. Max size 2MB.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Full Name</label>
              <input type="text" value={profile.name} onChange={(e) => setProfile({...profile, name: e.target.value})} className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-300 text-slate-900 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
              <input type="email" value={profile.email} onChange={(e) => setProfile({...profile, email: e.target.value})} className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-300 text-slate-900 text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Bio</label>
            <textarea value={profile.bio} onChange={(e) => setProfile({...profile, bio: e.target.value})} rows={3} className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-300 text-slate-900 text-sm" />
          </div>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-5 border-b border-slate-100"><h3 className="text-base font-semibold text-slate-900">Notification Settings</h3></div>
        <div className="p-6 space-y-5">
          {[
            { label: 'Email Notifications', desc: 'Receive email updates about your courses and students' },
            { label: 'Course Updates', desc: 'Get notified when students enroll or complete courses' },
            { label: 'Revenue Reports', desc: 'Monthly revenue and performance reports' },
          ].map((item) => (
            <div key={item.label} className="flex items-center justify-between">
              <div><h4 className="font-medium text-slate-900 text-sm">{item.label}</h4><p className="text-xs text-slate-500 mt-0.5">{item.desc}</p></div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" defaultChecked className="sr-only peer" />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-navy-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-navy-500"></div>
              </label>
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-end">
        <button className="bg-navy-500 hover:bg-navy-600 text-white px-6 py-2.5 rounded-lg transition-colors flex items-center space-x-2 text-sm font-medium">
          <Save className="w-4 h-4" /><span>Save Changes</span>
        </button>
      </div>
    </div>
  );
};

const CourseEditor: React.FC<{ course: Course | null; onSave: (course: Partial<Course>) => void; onCancel: () => void }> = ({ course, onSave, onCancel }) => {
  const [formData, setFormData] = useState({ title: course?.title || '', description: course?.description || '', category: course?.category || 'AI Trading', level: course?.level || 'Beginner', price: course?.price || 0, status: course?.status || 'draft' });
  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); onSave(formData); };
  const inputClass = "w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-300 text-slate-900 text-sm";

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-slate-900 font-serif">{course ? 'Edit Course' : 'Create New Course'}</h1>
          <div className="flex items-center space-x-3">
            <button onClick={onCancel} className="px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors text-sm">Cancel</button>
            <button onClick={handleSubmit} className="bg-navy-500 hover:bg-navy-600 text-white px-5 py-2 rounded-lg transition-colors flex items-center space-x-2 text-sm font-medium">
              <Save className="w-4 h-4" /><span>{course ? 'Update' : 'Create'}</span>
            </button>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto p-6">
        <form onSubmit={handleSubmit} className="space-y-5 bg-white rounded-xl border border-slate-200 p-6">
          <div><label className="block text-sm font-medium text-slate-700 mb-2">Course Title</label><input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-4 py-3 text-base border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-300 text-slate-900" placeholder="Enter course title..." required /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-2">Description</label><textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={4} className={inputClass} placeholder="Describe your course..." required /></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div><label className="block text-sm font-medium text-slate-700 mb-2">Category</label><select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className={inputClass}><option>AI Trading</option><option>Technical Analysis</option><option>Risk Management</option><option>Options Trading</option><option>Portfolio Management</option></select></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-2">Level</label><select value={formData.level} onChange={(e) => setFormData({...formData, level: e.target.value as any})} className={inputClass}><option>Beginner</option><option>Intermediate</option><option>Advanced</option></select></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-2">Price (₹)</label><input type="number" value={formData.price} onChange={(e) => setFormData({...formData, price: parseInt(e.target.value)})} className={inputClass} min="0" required /></div>
          </div>
          <div><label className="block text-sm font-medium text-slate-700 mb-2">Status</label><select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value as any})} className={inputClass}><option value="draft">Draft</option><option value="published">Published</option><option value="under_review">Under Review</option></select></div>
        </form>
      </div>
    </div>
  );
};

export default MentorDashboard;
